import { Alert, Badge, Button, Group, Stack, Table, Text, Tooltip } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useEscalonamentos, useResolverEscalonamento } from '@/hooks/useSoc'
import type { SocEscalation } from '@/api/soc'

/**
 * Os alertas do time que ainda pedem resposta — e o botão que os encerra.
 *
 * ESTA TELA NÃO EXISTIA. Até 14/09/2026 o `feed.py` calculava `aberto` e
 * `reconhecido` para o item do feed e NADA no frontend consumia: um alarme
 * podia estar no nível 2 há 40 minutos e não havia onde ver. É o mesmo modo de
 * falha que esta área vem consertando — o dado existe, o consumidor não.
 *
 * E o "Resolver" não é conveniência: até agora só a FONTE marcava um alerta
 * como resolvido, pelo `recovered` do Zabbix. O Wazuh teve 0 recuperações em 30
 * dias, porque `sshd` e força bruta são eventos pontuais e não estados que
 * voltam ao normal — sem este botão (e sem o `0` na ligação), um alerta de SIEM
 * no topo insistente tocaria para sempre.
 */

const RELOGIO: Record<SocEscalation['state'], string> = {
  pending: 'sobe de nível',
  acked: 'liga de novo',
  exhausted: 'liga de novo',
}

function Estado({ e }: { e: SocEscalation }) {
  if (e.state === 'exhausted') {
    return (
      <Tooltip label="Percorreu todos os níveis. Só resolver encerra.">
        <Badge color="red" variant="filled">
          no topo
        </Badge>
      </Tooltip>
    )
  }
  if (e.state === 'acked') {
    return (
      <Tooltip label="Alguém assumiu — mas assumir não é resolver: volta a ligar no prazo.">
        <Badge color="yellow" variant="light">
          assumido
        </Badge>
      </Tooltip>
    )
  }
  return (
    <Badge color="orange" variant="light">
      sem dono
    </Badge>
  )
}

function Quando({ iso }: { iso: string | null }) {
  if (!iso) {
    return (
      <Tooltip label="A política deste alerta não escalona (escalate_after_min vazio).">
        <Text size="sm" c="dimmed">
          não escalona
        </Text>
      </Tooltip>
    )
  }
  const d = new Date(iso)
  const faltam = Math.round((d.getTime() - Date.now()) / 60000)
  return (
    <Text
      size="sm"
      // Valor numérico é dado (DS §3.2), e o relógio correndo é o que a pessoa
      // olha primeiro nesta tela.
      style={{
        fontFamily: 'var(--mantine-font-family-monospace)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {faltam <= 0 ? 'agora' : `${faltam} min`}
    </Text>
  )
}

export function AlertasEmAndamento({ teamId }: { teamId: number }) {
  const { data: itens, isLoading } = useEscalonamentos(teamId)
  const resolver = useResolverEscalonamento(teamId)

  if (isLoading) return <Text size="sm" c="dimmed">Carregando…</Text>

  if (!itens?.length) {
    return (
      <Text size="sm" c="dimmed">
        Nenhum alerta em andamento neste time.
      </Text>
    )
  }

  const noTopo = itens.filter((e) => e.state === 'exhausted').length

  return (
    <Stack gap="sm">
      {noTopo > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {noTopo === 1
            ? '1 alerta chegou ao último nível e continua chamando.'
            : `${noTopo} alertas chegaram ao último nível e continuam chamando.`}{' '}
          Só resolver encerra.
        </Alert>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Alerta</Table.Th>
            <Table.Th w={110}>Nível</Table.Th>
            <Table.Th w={130}>Estado</Table.Th>
            {/* O cabeçalho diz O QUE vai acontecer; a célula, QUANDO. Sem isso
                "3 min" não diz se alguém vai ser acordado ou promovido. */}
            <Table.Th w={150}>Próxima ação</Table.Th>
            <Table.Th w={120} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {itens.map((e) => (
            <Table.Tr key={e.event_id}>
              <Table.Td>
                <Text size="sm">{e.titulo}</Text>
                <Text size="xs" c="dimmed">
                  {e.source} · aberto em{' '}
                  {new Date(e.aberto_desde).toLocaleString('pt-BR')}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text
                  size="sm"
                  style={{
                    fontFamily: 'var(--mantine-font-family-monospace)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {e.current_level}
                </Text>
              </Table.Td>
              <Table.Td>
                <Estado e={e} />
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <Quando iso={e.next_due_at} />
                  {e.next_due_at && (
                    <Text size="xs" c="dimmed">
                      {RELOGIO[e.state]}
                    </Text>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  loading={resolver.isPending}
                  onClick={() => resolver.mutate(e.event_id)}
                >
                  Resolver
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}
